import type { JSX } from 'react';
import type {
  AlternateAudioFile,
  ExtendedAudioDescriptionMp3File,
  MediaLocalization,
} from '~/graphql';
import { isNotNil, isNonEmptyString } from '~/utilities/type-guards';
import { useAlternateAudioFilesQuery } from '~/singlePageApp/screens/MediaCustomize/Customize/AudioDescriptions/__gql/AlternateAudioFiles.gql.gen';
import { useLoadFeatureFlags } from '~/singlePageApp/hooks/useLoadFeatureFlags';
import { useExtendedAudioDescriptionMp3FilesQuery } from '~/singlePageApp/screens/MediaCustomize/Customize/AudioDescriptions/__gql/ExtendedAudioDescriptionMp3Files.gql.gen';
import { useGetAlternateAudioLocalizationsQuery } from '~/singlePageApp/screens/MediaCustomize/Customize/AlternateAudioLocalizations/__gql/GetAlternateAudioLocalizations.gql.gen';
import { AudioDescriptionDownload } from './AudioDescriptionDownload';
import { ExtendedAudioDescriptionDownload } from './ExtendedAudioDescriptionDownload';
import { TabWrapper } from './SharedStyledComponents';
import { LocalizationDownload } from './LocalizationDownload';

type Props = {
  hashedId: string;
};

export const AudioTab = ({ hashedId }: Props): JSX.Element => {
  const { flags } = useLoadFeatureFlags(['flag_alternate_audio_localizations']);
  const isLocalizationsEnabled = flags.flag_alternate_audio_localizations.enabled;

  const { data: alternateAudioFilesData } = useAlternateAudioFilesQuery({
    variables: { hashedId },
  });

  const { data: extendedAudioFilesData } = useExtendedAudioDescriptionMp3FilesQuery({
    variables: { hashedId },
  });

  const { data: localizationFilesData } = useGetAlternateAudioLocalizationsQuery({
    variables: { mediaHashedId: hashedId },
    fetchPolicy: 'network-only',
    skip: !isLocalizationsEnabled,
  });

  const alternateAudioFiles = (alternateAudioFilesData?.media?.alternateAudioFiles ?? []).filter(
    ({ type }) => type !== 'Mp4AlternateAudioFile',
  );

  const extendedAudioFiles = (
    extendedAudioFilesData?.media?.extendedAudioDescriptionMp3Files ?? []
  ).filter(({ mediaExtendedAudioDescription }) => isNotNil(mediaExtendedAudioDescription));

  const localizationFiles = (localizationFilesData?.media?.localizations ?? []).filter(
    ({ downloadUrl }) => isNonEmptyString(downloadUrl),
  );

  return (
    <TabWrapper>
      {alternateAudioFiles.length > 0 && (
        <AudioDescriptionDownload
          alternateAudioFiles={alternateAudioFiles as AlternateAudioFile[]}
        />
      )}

      {extendedAudioFiles.length > 0 && (
        <ExtendedAudioDescriptionDownload
          extendedAudioFiles={extendedAudioFiles as ExtendedAudioDescriptionMp3File[]}
        />
      )}

      {isLocalizationsEnabled && localizationFiles.length > 0 && (
        <LocalizationDownload localizationFiles={localizationFiles as MediaLocalization[]} />
      )}
    </TabWrapper>
  );
};
